import * as THREE from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader'; //loader for RGBE format (Radiance HDR)
import { UltraHDRLoader } from 'three/examples/jsm/loaders/UltraHDRLoader.js';
import { EffectPass, SelectiveBloomEffect, BloomEffect, BlendFunction, ToneMappingEffect, ToneMappingMode, EffectComposer, RenderPass, KernelSize, SMAAEffect, SMAAPreset, DepthOfFieldEffect } from "postprocessing";
import { HalfFloatType } from "three";
import { GodraysPass } from 'three-good-godrays';
import { RedScreenEffect } from './RedScreenEffect';
import { FogEffect } from './FogEffect';

export default class WorldScene {
    constructor(container, visitedFromMobileDevice) {
        this.container = container;
        this.init();
        this.addLights();
        this.addFog();
        this.addDefaultPostProcessing();
        this.addInteriorFactoryPostProcessing();

        this.envMap = null;
        //this.addSkybox(visitedFromMobileDevice);

    }

    init() {
        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(65, window.innerWidth / window.innerHeight, 0.1, 1800);

        this.renderer = new THREE.WebGLRenderer({
            powerPreference: "high-performance",
            antialias: false,
            stencil: false,
            depth: false,
            depthTexture: false,
            logarithmicDepthBuffer: false,
            precision: "mediump",
            alpha: false
        });
        let pixelRatio = window.devicePixelRatio;
        //this.renderer.setPixelRatio(pixelRatio > 1.5 ? 1.0 : pixelRatio);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.1));
        //this.renderer.setPixelRatio(1.05);
        this.renderer.outputEncoding = THREE.SRGBColorSpace;
        //this.renderer.setClearColor(0x000000, 0);

        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.shadowMap.autoUpdate = true;

        this.renderer.toneMappingExposure = 0.24;
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.container.appendChild(this.renderer.domElement);

        this.composerDefault = new EffectComposer(this.renderer, {
            frameBufferType: HalfFloatType,
            multisampling: 4
        });

        this.composerFactoryInterior = new EffectComposer(this.renderer, {
            multisampling: 4,
            frameBufferType: HalfFloatType
        });


        // Resize Observer
        this.resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                const { width, height } = entry.contentRect;
                this.renderer.setSize(width, height);
                this.composerDefault.setSize(width, height);
                this.composerFactoryInterior.setSize(width, height);
                this.camera.aspect = width / height;
                this.camera.updateProjectionMatrix();

            }
        });

        this.resizeObserver.observe(this.container);

    }

    addLights() {
        //const dayLight = new THREE.HemisphereLight(0xfff2c7, 0x080820, 20.35);
        //const dayLight = new THREE.DirectionalLight(0xfff2c7, 1.2)
        //fff2c7
        //F2E6BD
        //dayLight.position.set(0, 50, 50)
        //f2e1aa
        //F2E6BD
        //this.scene.add(dayLight);

        //const helperDir = new THREE.DirectionalLightHelper(dayLight, 5);
        //worldScene.scene.add(helperDir);

        const widthLight = 200;
        const height = 90;
        const intensity = 5; //9
        //const rectLight = new THREE.RectAreaLight(0xf1f3e1, intensity, widthLight, height);
        //0xa9dbfd
        const rectLight = new THREE.RectAreaLight(0xF2E6BD, intensity, widthLight, height);
        const rectLight2 = new THREE.RectAreaLight(0xF2E6BD, intensity, 320, 80); //8
        //82ccff
        rectLight2.position.set(0, 0, 0);
        rectLight2.lookAt(0, 0, 100);

        rectLight.position.set(0, 40, 145);
        rectLight.lookAt(0, 0, 125);
        this.scene.add(rectLight2)
        this.scene.add(rectLight)
    }

    addFog() {
        this.scene.fog = new THREE.FogExp2(0x000000, 0.0015); // 0.0017 is the density of the fog
        //56515f
        //45414d
        //44424d


        //4b4856
        //56545c
        //64616b
        //9692a1
        //9998ab
        //f2dfa0

    }

    addDefaultPostProcessing() {

        const effectTonemapping = new ToneMappingEffect({
            blendFunction: BlendFunction.SRC,
            mode: ToneMappingMode.ACES_FILMIC
        });

        const effectBloom = new SelectiveBloomEffect(this.scene, this.camera, {
            blendFunction: BlendFunction.ADD,
            mipmapBlur: true,
            levels: 6,
            luminanceThreshold: 1.0,
            luminanceSmoothing: 0.9,
            intensity: 0.65,
            opacity: 1.0,
            radius: 0.9,
            kernelSize: KernelSize.MEDIUM
        });

        // // Depth of Field effect - exposed for tweaking
        // this.dofEffect = new DepthOfFieldEffect(this.camera, {
        //     focusDistance: 0.4,    // focus distance in world units (0 = auto)
        //     focalLength: 2.2,     // focal length of the camera lens
        //     bokehScale: 3.0,       // blur scale / bokeh size
        //     resolutionScale: 0.5   // lower = more performance, less quality
        // });
        // this.dofEffect.blendMode.opacity.value = 1.0; // disabled by default

        this.composerDefault.addPass(new RenderPass(this.scene, this.camera));
        this.composerDefault.addPass(new EffectPass(this.camera, effectBloom, effectTonemapping));


    }

    addInteriorFactoryPostProcessing() {

        const effectTonemapping = new ToneMappingEffect({
            blendFunction: BlendFunction.SRC,
            mode: ToneMappingMode.ACES_FILMIC
        });

        const godraysParams = {
            density: 0.002, //0.0040
            maxDensity: 0.01,
            edgeStrength: 5.0,
            edgeRadius: 2.0,
            distanceAttenuation: 2.0, //3.1
            color: new THREE.Color(0xFFB55C),
            raymarchSteps: 30,
            blur: true,
            blurVariance: 0.1,
            //blurKernelSize: SMALL,
            gammaCorrection: true,
        };

        // godrays can be cast from either `PointLight`s or `DirectionalLight`s
        const lightPos = new THREE.Vector3(1420, 140, 1300);
        const dirLight = new THREE.DirectionalLight(0xffffff, 0.0);
        dirLight.castShadow = true;
        dirLight.shadow.mapSize.width = 1024;
        dirLight.shadow.mapSize.height = 1024;
        dirLight.shadow.camera.near = 10;
        dirLight.shadow.camera.far = 500;
        dirLight.shadow.camera.left = -340;
        dirLight.shadow.camera.right = 150;
        dirLight.shadow.camera.top = 60;
        dirLight.shadow.camera.bottom = -110;
        dirLight.shadow.camera.updateProjectionMatrix();
        dirLight.shadow.autoUpdate = true;
        dirLight.position.copy(lightPos).add(new THREE.Vector3(0, 0, 10));
        //dirLight.target.position.set(1200, 50, 800);
        dirLight.target.position.set(1200, 5, 600);
        dirLight.target.updateMatrixWorld();
        this.scene.add(dirLight.target);
        this.scene.add(dirLight);

        //const dirLightHelper = new THREE.DirectionalLightHelper(dirLight, 5);
        //worldScene.scene.add(dirLightHelper);
        //const dirLightCameraHelper = new THREE.CameraHelper(dirLight.shadow.camera);
        //worldScene.scene.add(dirLightCameraHelper);

        const godraysPass = new GodraysPass(dirLight, this.camera, godraysParams);
        //const smaaEffect = new SMAAEffect({ preset: SMAAPreset.ULTRA });

        this.composerFactoryInterior.addPass(new RenderPass(this.scene, this.camera));
        this.composerFactoryInterior.addPass(new EffectPass(this.camera, effectTonemapping));
        this.composerFactoryInterior.addPass(godraysPass);
        //this.composerFactoryInterior.addPass(new EffectPass(this.camera, smaaEffect));
    }



    loadSkybox() {
        const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
        pmremGenerator.compileEquirectangularShader();

        this.skyboxPromise = new Promise((resolve) => {
            const ultraHDRLoader = new UltraHDRLoader();
            ultraHDRLoader.setDataType(THREE.HalfFloatType);
            ultraHDRLoader.load('./assets/envMap.jpg', (texture) => {
                texture.mapping = THREE.EquirectangularReflectionMapping;

                this.envMap = pmremGenerator.fromEquirectangular(texture).texture;
                this.scene.background = this.envMap;

                pmremGenerator.dispose();

                // Resolve with original equirectangular texture (for ground material etc.)
                resolve(texture);
            });
        });
    }

    /* --- ORIGINAL RGBELoader skybox method (revert by swapping with loadSkybox above) ---
    loadSkybox(visitedFromMobileDevice) {
        const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
        pmremGenerator.compileEquirectangularShader();

        const skyboxToLoad = visitedFromMobileDevice ? 'belfast_sunset_puresky_1k' : 'belfast_sunset_puresky_1k';

        this.skyboxPromise = new Promise((resolve) => {
            const rgbeLoader = new RGBELoader();
            rgbeLoader.load(`./assets/${skyboxToLoad}.hdr`, (texture) => {
                texture.mapping = THREE.EquirectangularReflectionMapping;

                this.envMap = pmremGenerator.fromEquirectangular(texture).texture;
                this.scene.background = this.envMap;

                pmremGenerator.dispose();
                resolve(texture);
            });
        });
    }
    --- END ORIGINAL RGBELoader skybox method --- */

    getRenderer() {
        return this.renderer;
    }

    getCamera() {
        return this.camera;
    }

    getComposerDefault() {
        return this.composerDefault;
    }

    getComposerFactoryInterior() {
        return this.composerFactoryInterior;
    }
}
